import { Component, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PlIconModule } from '@app/icon-library/lib/pl-icon.module';
import { PlIcon } from '@app/icon-library/icons';
import { EntityStatus } from '@shared/models/menu.model';
import { ComponentChanges } from '@app/utils/simpleChangesType';
import { ButtonModule } from '@app/components/button/button.module';
import { ButtonVariant } from '@app/components/button/button.component';

export enum ChevronDirection {
  right = 'right',
  up = 'up',
  down = 'down',
}

interface StatusIcon {
  iconName: PlIcon;
  iconColorCssClass: string | null;
}

@Component({
  selector: 'pl-life-acts-menu-item',
  template: `
    <pl-button
      [variant]="isSelected ? ButtonVariant.blue : ButtonVariant.lightGray"
      [isDisabled]="isDisabled"
      class=""
      (click)="onMenuItemClicked()"
    >
      <div class="flex w-full items-center justify-between rounded-md gap-2 ">
        <div class="relative w-6 h-6 rounded-full bg-white">
          <pl-icon
            *ngIf="statusIcon"
            [name]="statusIcon.iconName"
            [ngClass]="statusIcon.iconColorCssClass"
            class="absolute top-0 left-0 w-6 h-6"
          />
        </div>
        <p class="text-inherit text-left text-lg font-bold grow">{{ title }}</p>
        <pl-icon
          [name]="PlIcon.chevron_forward"
          class="text-inherit transition-all transform duration-300 w-5 h-5"
          [ngClass]="{
            '-rotate-90': chevronDirection === ChevronDirection.up,
            'rotate-90': chevronDirection === ChevronDirection.down
          }"
        />
      </div>
    </pl-button>
  `,
  standalone: true,
  imports: [CommonModule, PlIconModule, ButtonModule],
})
export class LifeActsMenuItemComponent implements OnInit, OnChanges {
  @Input({ required: true }) public chevronDirection!: ChevronDirection;
  @Input({ required: true }) public isDisabled!: boolean;
  @Input({ required: true }) public isSelected!: boolean;
  @Input({ required: true }) public status!: EntityStatus;
  @Input({ required: true }) public title!: string;

  @Output() public menuItemClicked: EventEmitter<void> = new EventEmitter<void>();

  public statusIcon: StatusIcon | null = null;

  protected readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;
  protected readonly ChevronDirection: typeof ChevronDirection = ChevronDirection;
  protected readonly EntityStatus: typeof EntityStatus = EntityStatus;
  protected readonly PlIcon: typeof PlIcon = PlIcon;

  @HostBinding('class') public classes: string = 'block';

  constructor() {}

  public ngOnChanges(changes: ComponentChanges<LifeActsMenuItemComponent>): void {
    if (changes.status || changes.isSelected) {
      this.statusIcon = this.getStatusIconFromStatusAndSelected(this.status, this.isSelected);
    }
  }

  private getStatusIconFromStatusAndSelected(status: EntityStatus, isSelected: boolean): StatusIcon | null {
    switch (status) {
      case EntityStatus.locked:
        return {
          iconName: PlIcon.lock_inverted_circle,
          iconColorCssClass: null,
        };

      case EntityStatus.completed:
        if (isSelected) {
          return {
            iconName: PlIcon.tick,
            iconColorCssClass: 'text-pl-gray-500',
          };
        } else {
          return {
            iconName: PlIcon.check_inverted_circle,
            iconColorCssClass: 'text-pl-green-500',
          };
        }

      case EntityStatus.current:
        if (isSelected) {
          return {
            iconName: PlIcon.eye,
            iconColorCssClass: 'text-pl-blue-500 p-0.5',
          };
        } else {
          return {
            iconName: PlIcon.eye_inverted_circle,
            iconColorCssClass: 'text-pl-blue-500',
          };
        }

      default:
        return null;
    }
  }

  public async ngOnInit(): Promise<void> {}

  public onMenuItemClicked(): void {
    console.log('TONIO LifeActsMenuItemComponent onMenuItemClicked title=', this.title);
    this.menuItemClicked.emit();
  }
}
